import { useState, useCallback } from 'react';

/**
 * Custom hook to manage API call state (loading, error, data)
 * Eliminates duplicate state management across components
 *
 * @param {*} initialData - Initial value for data state (default: null)
 * @returns {Object} API state and handlers
 *
 * @example
 * const { data: users, loading, error, executeRequest } = useApiState([]);
 *
 * useEffect(() => {
 *   executeRequest(() => adminAPI.getAllUsers());
 * }, []);
 */
export const useApiState = (initialData = null) => {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * Execute an async API call with automatic state management
   * Handles loading, error, and data state automatically
   *
   * @param {Function} apiCall - Async function that returns a promise
   * @returns {Promise} Result of the API call
   *
   * @example
   * await executeRequest(() => adminAPI.createUser(userData));
   */
  const executeRequest = useCallback(async (apiCall) => {
    setLoading(true);
    setError('');
    try {
      const result = await apiCall();
      setData(result);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - function is stable across renders

  /**
   * Reset all state to initial values
   */
  const reset = () => {
    setData(initialData);
    setLoading(true);
    setError('');
  };

  return {
    data,
    setData,
    loading,
    setLoading,
    error,
    setError,
    executeRequest,
    reset
  };
};
